import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import '../styles/Compare.css';

const STORAGE_KEY = 'miraijuku.univCompare.v1';

const emptyRow = () => ({
  name: '',
  url: '',
  track: '',
  ejuSubjects: '',
  english: '',
  examExtra: '',
  deadline: '',
  notes: '',
});

const loadRows = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [emptyRow(), emptyRow(), emptyRow()];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length < 3) return [emptyRow(), emptyRow(), emptyRow()];
    return arr.slice(0, 3).map((r) => ({ ...emptyRow(), ...r }));
  } catch {
    return [emptyRow(), emptyRow(), emptyRow()];
  }
};

const FIELDS = [
  { key: 'name', label: '대학·학부' },
  { key: 'url', label: '요강/입시 URL' },
  { key: 'track', label: '전형 (유학생/일반 등)' },
  { key: 'ejuSubjects', label: 'EJU·시험 과목' },
  { key: 'english', label: '영어 외부시험' },
  { key: 'examExtra', label: '면접·소논문 등' },
  { key: 'deadline', label: '접수·필착·시험일' },
  { key: 'notes', label: '메모' },
];

const Compare = () => {
  const [rows, setRows] = useState(() => loadRows());
  const [copied, setCopied] = useState(false);

  usePageMeta({
    title: '대학 3곳 비교',
    description:
      '관심 대학 요강 URL·과목·일정을 나란히 메모합니다. 이 기기에만 저장되며 합격선은 넣지 마세요.',
    path: '/compare',
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  }, [rows]);

  const setCell = (index, key, value) => {
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const clearAll = () => {
    if (window.confirm('비교표를 비울까요?')) {
      setRows([emptyRow(), emptyRow(), emptyRow()]);
    }
  };

  const buildText = () => {
    const lines = ['[미라이쥬쿠 대학 비교표]', ''];
    rows.forEach((row, i) => {
      lines.push(`## 대학 ${i + 1}: ${row.name || '(이름 없음)'}`);
      FIELDS.forEach((f) => {
        if (f.key === 'name') return;
        lines.push(`- ${f.label}: ${row[f.key] || '-'}`);
      });
      lines.push('');
    });
    lines.push('※ 최종 근거는 각 대학 해당 연도 모집요항 원문입니다.');
    return lines.join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="apple-page apple-compare">
      <header className="apple-page-hero">
        <p className="apple-eyebrow">Compare</p>
        <h1 className="apple-display">대학 3곳 비교</h1>
        <p className="apple-lede">
          관심 대학의 요강 URL과 과목·일정을 직접 적어 나란히 봅니다. 로그인 없이 이 기기에만
          저장됩니다. 합격선 숫자는 넣지 마세요.
        </p>
      </header>

      <div className="apple-compare-toolbar no-print">
        <button type="button" className="apple-btn apple-btn-secondary" onClick={handleCopy}>
          {copied ? '복사됨' : '텍스트 복사'}
        </button>
        <button type="button" className="apple-btn apple-btn-secondary" onClick={() => window.print()}>
          인쇄
        </button>
        <button type="button" className="apple-btn apple-btn-secondary" onClick={clearAll}>
          초기화
        </button>
      </div>

      <div className="apple-compare-scroll">
        <table className="apple-compare-table">
          <thead>
            <tr>
              <th scope="col">항목</th>
              <th scope="col">대학 1</th>
              <th scope="col">대학 2</th>
              <th scope="col">대학 3</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((field) => (
              <tr key={field.key}>
                <th scope="row">{field.label}</th>
                {rows.map((row, i) => (
                  <td key={i}>
                    {field.key === 'notes' || field.key === 'ejuSubjects' ? (
                      <textarea
                        rows={3}
                        value={row[field.key]}
                        onChange={(e) => setCell(i, field.key, e.target.value)}
                        placeholder="요강에서 옮겨 적기"
                      />
                    ) : (
                      <input
                        type="text"
                        value={row[field.key]}
                        onChange={(e) => setCell(i, field.key, e.target.value)}
                        placeholder={field.key === 'url' ? 'https://…' : ''}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <aside className="apple-article-note">
        이 표는 메모용입니다. 지원 직전 각 URL의 해당 연도 PDF로 다시 대조하세요.
      </aside>

      <div className="apple-page-foot no-print">
        <Link to="/checklist" className="apple-btn apple-btn-primary">
          요강 체크리스트
        </Link>
        <Link to="/finder" className="apple-link">
          루트 파인더
        </Link>
        <Link to="/guides/example-national-stem" className="apple-link">
          대학 예시 가이드
        </Link>
      </div>
    </div>
  );
};

export default Compare;
